import React, { useState, useCallback } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { FlexyIconButton, ModalContext } from '@europrocurement/flexy-components';
import { editIcon } from '@europrocurement/l2d-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    UPDATE_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
} from '@europrocurement/l2d-modules/modules/Dossiers/providers';
import { UpdateDossierPayload } from '@europrocurement/l2d-modules/modules/Dossiers/types/UpdateDossierPayload';
import { useDossier } from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import { EditFolioSocieteRefForm } from '../../../shared/EditFolioSocieteRefForm/EditFolioSocieteRefForm';

interface ClientReferenceProps {
    societeRef?: string;
}

export const ClientReference: React.FunctionComponent<ClientReferenceProps> = function ({
    societeRef,
}) {
    const { modalActions } = React.useContext(ModalContext);
    const [refHovered, setRefHovered] = useState(false);
    const { dossier } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();
    const { updateDossier } = useDossier();
    const { palette } = useTheme();

    const handleCancel = useCallback(() => {
        modalActions.close();
    }, [modalActions]);

    const handleSuccess = useCallback(async () => {
        try {
            setRefHovered(false);
            modalActions.close();
        } catch (error) {
            console.error('Erreur lors de la mise à jour:', error);
        }
    }, [modalActions]);

    const openEditFolioSocieteRefForm = useCallback(() => {
        if (!dossier) {
            return;
        }
        modalActions.call(
            <EditFolioSocieteRefForm
                onCancel={handleCancel}
                onSuccess={handleSuccess}
                dossier={dossier}
                updateDossier={async (payload: UpdateDossierPayload) => {
                    try {
                        const updatedDossier = await updateDossier(dossier.id as number, payload);
                        if (updatedDossier) {
                            if (dispatch) {
                                dispatch({
                                    type: UPDATE_CURRENT_DOSSIER_CTX,
                                    payload: {
                                        dossierId: dossier.id as number,
                                        data: {
                                            ...dossier,
                                            societeRef: payload.societeRef,
                                        },
                                    },
                                });
                            }
                        }
                    } catch (error) {
                        console.error('Erreur lors de la mise à jour:', error);
                    }
                }}
            />,
        );
    }, [dispatch, dossier, handleCancel, handleSuccess, modalActions, updateDossier]);

    const editFolioSocieteRef = useCallback(() => {
        if (!dossier) return;

        openEditFolioSocieteRefForm();
    }, [dossier, openEditFolioSocieteRefForm]);

    const handleDeleteSocieteRef = useCallback(async () => {
        if (!dossier) {
            return;
        }

        try {
            const updatedDossier = await updateDossier(dossier.id as number, { societeRef: '' });
            if (updatedDossier) {
                if (dispatch) {
                    dispatch({
                        type: UPDATE_CURRENT_DOSSIER_CTX,
                        payload: {
                            dossierId: dossier.id as number,
                            data: {
                                ...dossier,
                                societeRef: '',
                            },
                        },
                    });
                }
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour:', error);
        }
    }, [dossier, updateDossier, dispatch]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                position: 'relative',
                padding: '4px 0',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={() => setRefHovered(true)}
            onMouseLeave={() => setRefHovered(false)}
        >
            <Typography
                variant="l2dbodys"
                color={palette.grey[600]}
            >
                Référence client:
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {societeRef ? (
                    <Typography
                        variant="l2dbodys"
                        color={palette.grey[700]}
                        sx={{ fontWeight: 500 }}
                    >
                        {societeRef}
                    </Typography>
                ) : (
                    <Typography
                        variant="l2dbodys"
                        color={palette.grey[50]}
                        sx={{ fontStyle: 'italic' }}
                    >
                        Non renseignée
                    </Typography>
                )}

                {/* Boutons d'action avec effet slide */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: '4px',
                        opacity: refHovered ? 1 : 0,
                        transform: refHovered ? 'translateX(0)' : 'translateX(-8px)',
                        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    }}
                >
                    {/* Bouton d'édition */}
                    <FlexyIconButton
                        displayTooltip
                        icon={editIcon}
                        onClick={editFolioSocieteRef}
                        tooltipOverwriteProps={{
                            title: 'Éditer la référence client',
                        }}
                        iconOverwriteProps={{
                            size: '2xs',
                            sx: {
                                '&:hover': {
                                    color: palette.primary.main,
                                },
                            },
                        }}
                    />

                    {/* Bouton de suppression (si référence existe) */}
                    {societeRef && (
                        <FlexyIconButton
                            displayTooltip
                            icon={['fasl', 'times'] as IconProp}
                            onClick={handleDeleteSocieteRef}
                            tooltipOverwriteProps={{
                                title: 'Supprimer la référence client',
                            }}
                            iconOverwriteProps={{
                                size: '2xs',
                                sx: {
                                    '&:hover': {
                                        color: palette.error.main,
                                    },
                                },
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
};
